import { createUserManager, signoutRedirect } from "./userService";
import store from "../redux/store";
import { setAuth } from "../redux/authSlice";
import { setAuthHeader } from "../utils/axiosHeader";
import axios from "../api/axiosClient";
import { toast } from "react-hot-toast";
import { useEffect } from "react";
import { getMaintenanceInfo } from "./maintenanceService";
import { setMaintenanceInfo } from "../utils/maintenanceStore";

export const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL;

export const useKeyDown = (callback, keys) => {
  const onKeyDown = (event) => {
    const wasAnyKeyPressed = keys.some((key) => event.key === key);
    if (wasAnyKeyPressed) {
      event.preventDefault();
      callback();
    }
  };
  useEffect(() => {
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onKeyDown]);
};

const exchangeRefreshTokenForAccessToken = async (userManager) => {
  try {
    // The library will use the refresh token grant to get a new access token
    await userManager.signinSilent();
  } catch (e) {
    // When the session expires this will fail with an 'invalid_grant' response
    if (e.message === "invalid_grant") {
      signoutRedirect(userManager);
    } else if (
      e.message &&
      e.message.startsWith("Cannot read properties of null")
    ) {
      // do nothing
    } else {
      throw e;
    }
  }
};

async function renewAccessToken(userManager) {
  let user = await userManager.getUser();

  if (user && user.refresh_token && user.refresh_token.length > 0) {
    await exchangeRefreshTokenForAccessToken(userManager);
  } else {
    signoutRedirect(userManager);
  }

  user = await userManager.getUser();

  if (user && user.access_token) {
    setAuthHeader(user.access_token);
    store.dispatch(setAuth({ user: user }));
    return new Promise((resolve) => {
      setTimeout(() => resolve(user), 100);
    });
  } else {
    return Promise.reject(user);
  }
}

axios.interceptors.request.use(
  async function (req) {
    if (window.authenticationConfig !== undefined) {
      let userManager = createUserManager();
      let user = await userManager.getUser();
      if (user) {
        if (user.expired) {
          await renewAccessToken(userManager).then(
            (success) => {
              return req;
            },
            (err) => {
              signoutRedirect(userManager);
            }
          );
        }
      }
    }
    return req;
  },
  (err) => {
    return Promise.reject(err);
  }
);

export function dataURLtoFile(dataurl, filename) {
  var arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[arr.length - 1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

export function getFile(url, successCallback, errorCallback, cancelToken) {
  let config = { cancelToken: cancelToken };
  config.responseType = "blob";
  config.headers = { "Content-Type": "application/json" };

  return axios
    .get(url, config)
    .then((response) => {
      let fileName = "1.tmp";
      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const asciiFilenameRegex = /filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i;
        const matches = asciiFilenameRegex.exec(contentDisposition);
        if (matches != null && matches[2]) {
          fileName = matches[2];
        }
      }

      const contentType = response.headers["content-type"];

      const blob = response.data;
      return new File([blob], fileName, { type: contentType });
    })
    .then((data) => {
      successCallback(data);
    })
    .catch((error) => {
      errorCallback(error);
    });
}

export const addDownloadLinkToDocument = (file, mimeType, fileName) => {
  const url = window.URL.createObjectURL(
    new Blob([file], {
      type: mimeType,
    })
  );
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);

  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
};

export function handleError(error, intl, errorCallback) {
  if(error.response == undefined) {
    if (error.code !== "ERR_CANCELED") {
      getMaintenanceInfo((info) => {
        if (info && info.isActive) {
          setMaintenanceInfo(info);
        }
      }, () => {});
    }
    return;
  }

  if (errorCallback == undefined) {
    errorCallback = () => errorToast(error, intl);
  }

  if (error.response.data instanceof Blob) {
    getErrorFromBlob(error.response.data).then((err) => {
      error.response.data = err;
      errorCallback(error.response.data);
    });
  } else if (error.response.status === 401) {
    let userManager = createUserManager();
    signoutRedirect(userManager);
  } else {
    errorCallback();
  }
}

async function getErrorFromBlob(errBlob) {
  let err = {};
  try {
    const errJson = await errBlob.text();
    err = errJson && errJson.length > 0 ? JSON.parse(errJson) : {};
  } catch (error) {
    console.error(error);
  }
  return err;
}

export function errorToast(error, intl) {
  var message = intl.formatMessage({ id: "Error.GenericError" });
  if (error.response.data == null || error.response.data.type == null) {
    toast.error(message);
  } 
  // else if(error.code == "ERR_BAD_REQUEST"){
  //   message = intl.formatMessage({ id: "Error.BadRequest" });
  //   toast.error(message);
  // }
  else {
    message = intl.formatMessage({ id: `Error.${error.response.data.type}` });
    toast.error(message);
  }
}

export function getFilledOrNullString(text) {
  if (text != undefined && text != "") {
    return text;
  }
  return null;
}

export function getDateAsISOString(date) {
  if (date !== undefined && date !== "" && date !== null) {
    return date.toISOString().substring(0, 10);
  }
  return null; //?
}

export function getFilledOrEmptyArray(array) {
  var result = [];
  if (array !== undefined && Array.isArray(array)) {
    array.forEach((x) => result.push(x));
  }
  return result;
}

export function combineStrings(strings, separator = "") {
  if (!Array.isArray(strings) || strings.length === 0) return "";
  const cleaned = strings.filter(s => s != null);
  if (cleaned.length === 0) return "";
  return cleaned.map(String).join(separator);
}
